import { useState, useEffect } from "react";

import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

interface CreateVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  initialTemplateId?: string;
}

export const CreateVersionDialog = ({
  open,
  onOpenChange,
  onSuccess,
  initialTemplateId,
}: CreateVersionDialogProps) => {
  const { invokeAdmin, isLoading } = useAdminEdgeFunction();
  const [formData, setFormData] = useState({
    template_id: initialTemplateId || "",
    prompt_text: "",
    change_summary: "",
  });

  const { data: templates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ["prompt-templates-for-version"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_templates" as any)
        .select("id, name, function_name")
        .eq("status", "active");

      if (error) {
        console.error("Error fetching templates:", error);
        throw error;
      }
      return data;
    },
    enabled: open,
  });

  // Update template_id when initialTemplateId changes or dialog opens
  useEffect(() => {
    if (open && initialTemplateId) {
      setFormData(prev => ({ ...prev, template_id: initialTemplateId }));
    }
  }, [open, initialTemplateId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { error } = await invokeAdmin("create-prompt-version", {
      body: formData,
      showErrorToast: true,
    });

    if (!error) {
      onSuccess();
      setFormData({ template_id: "", prompt_text: "", change_summary: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Prompt Version</DialogTitle>
          <DialogDescription>
            Create a new version of an existing prompt template
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="template">Template</Label>
            <Select
              value={formData.template_id}
              onValueChange={(value) => setFormData({ ...formData, template_id: value })}
              required
              disabled={isLoadingTemplates}
            >
              <SelectTrigger>
                <SelectValue placeholder={isLoadingTemplates ? "Loading templates..." : "Select a template"} />
              </SelectTrigger>
              <SelectContent>
                {templates?.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    No active templates found
                  </div>
                )}
                {templates?.map((template: any) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} ({template.function_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt_text">Prompt Text</Label>
            <Textarea
              id="prompt_text"
              value={formData.prompt_text}
              onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
              placeholder="Enter the prompt text for this version..."
              className="min-h-[200px] font-mono text-sm"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="change_summary">Change Summary</Label>
            <Textarea
              id="change_summary"
              value={formData.change_summary}
              onChange={(e) => setFormData({ ...formData, change_summary: e.target.value })}
              placeholder="Describe what changed in this version..."
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Version"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
